/**
 * Hook for managing periodic notification synchronization
 * Ensures notifications stay up to date even when SSE is connected
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useRefreshNotifications } from './useNotificationQueries';

interface UseNotificationSyncOptions {
  enabled?: boolean;
  syncInterval?: number; // milliseconds
  syncOnFocus?: boolean;
  syncOnReconnect?: boolean;
}

export const useNotificationSync = (options: UseNotificationSyncOptions = {}) => {
  const {
    enabled = true,
    syncInterval = 5 * 60 * 1000, // Default 5 minutes
    syncOnFocus = true,
    syncOnReconnect = true
  } = options;

  const { isApiAuthReady } = useAuthStore();
  const { refreshAll } = useRefreshNotifications();
  
  const lastSyncRef = useRef<number>(0);
  const isDocumentHiddenRef = useRef<boolean>(false);

  // Sync function with throttling
  const performSync = useCallback(async (reason: string) => {
    if (!isApiAuthReady || !enabled) return;

    const now = Date.now();
    const timeSinceLastSync = now - lastSyncRef.current;
    
    // Throttle syncs to prevent excessive API calls
    if (timeSinceLastSync < 60000) { // 🔥 IMPROVED: Reduce throttle to 1 minute for better responsiveness when returning to browser
      console.log(`🔄 [NotificationSync] Skipping sync (${reason}) - throttled (${timeSinceLastSync}ms since last)`);
      return;
    }

    try {
      console.log(`🔄 [NotificationSync] Performing sync (${reason})`);
      lastSyncRef.current = now;
      
      // Refresh all notification queries
      await refreshAll();
      
      console.log(`✅ [NotificationSync] Sync completed (${reason})`);
    } catch (error) {
      console.error(`❌ [NotificationSync] Sync failed (${reason}):`, error);
    }
  }, [isApiAuthReady, enabled, refreshAll]);

  // Periodic sync interval
  useEffect(() => {
    if (!enabled || !isApiAuthReady) return;

    console.log(`🔄 [NotificationSync] Setting up periodic sync (${syncInterval}ms)`);
    
    const interval = setInterval(() => {
      // Only sync if document is visible to avoid unnecessary API calls
      if (!document.hidden) {
        performSync('periodic');
      }
    }, syncInterval);

    return () => {
      console.log(`🔄 [NotificationSync] Cleaning up periodic sync`);
      clearInterval(interval);
    };
  }, [enabled, isApiAuthReady, syncInterval, performSync]);

  // Sync on window focus
  useEffect(() => {
    if (!syncOnFocus || !enabled) return;

    const handleFocus = () => {
      // Only sync if we've been away for more than 2 minutes
      const timeSinceLastSync = Date.now() - lastSyncRef.current;
      if (timeSinceLastSync > 120000) { // 🔥 FIXED: Increase to 2 minutes
        performSync('window-focus');
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isDocumentHiddenRef.current = true;
      } else if (isDocumentHiddenRef.current) {
        // Document just became visible after being hidden
        isDocumentHiddenRef.current = false;
        const timeSinceLastSync = Date.now() - lastSyncRef.current;
        if (timeSinceLastSync > 120000) { // 🔥 FIXED: Increase to 2 minutes
          performSync('visibility-change');
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncOnFocus, enabled, performSync]);

  // Sync on network reconnection
  useEffect(() => {
    if (!syncOnReconnect || !enabled) return;

    const handleOnline = () => {
      performSync('network-reconnect');
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncOnReconnect, enabled, performSync]);

  // Manual sync function for external use
  const manualSync = useCallback(() => {
    return performSync('manual');
  }, [performSync]);

  // Check if notifications are stale (last sync was more than sync interval ago)
  const isStale = useCallback(() => {
    const timeSinceLastSync = Date.now() - lastSyncRef.current;
    return timeSinceLastSync > syncInterval;
  }, [syncInterval]);

  return {
    manualSync,
    isStale,
    lastSyncTime: lastSyncRef.current
  };
};